import { defaultCurrency, defaultLocale, defaultShippingAddress } from '~/api';
import type { Aggregator, ShippingAddress } from '~/api/types';

export interface ShipwreckConfiguration {
  aggregator: Aggregator;
  shipping_address: ShippingAddress;
  locale: string;
  currency: string;
}

/** Every field is settable from the URL, and every field is persisted back to it. */
export function readConfigurationFromQuery(
  search: string,
): ShipwreckConfiguration {
  const query = new URLSearchParams(search);
  const get = (key: string) => query.get(key) ?? '';

  const identifier = get('identifier');

  return {
    aggregator: {
      endpoint: get('endpoint'),
      identifier,
      key: get('key'),
    },
    shipping_address: {
      ...defaultShippingAddress,
      city: get('city') || defaultShippingAddress.city,
      country: get('country') || defaultShippingAddress.country,
      postal_code: get('postcode') || defaultShippingAddress.postal_code,
      street_address: get('street') || defaultShippingAddress.street_address,
    },
    locale: get('locale') || defaultLocale,
    currency: (get('currency') || defaultCurrency).toUpperCase(),
  };
}

/** Values still at their default are left out so shared links stay short. */
const whenChanged = (value: string, defaultValue: string) =>
  value && value !== defaultValue ? value : undefined;

export function writeConfigurationToQuery(config: ShipwreckConfiguration) {
  const {
    aggregator,
    shipping_address: address,
    locale,
    currency,
  } = config;

  const entries: Record<string, string | undefined> = {
    endpoint: aggregator.endpoint || undefined,
    identifier: aggregator.identifier || undefined,
    key: aggregator.key || undefined,

    city: whenChanged(address.city, defaultShippingAddress.city),
    country: whenChanged(address.country, defaultShippingAddress.country),
    postcode: whenChanged(
      address.postal_code,
      defaultShippingAddress.postal_code,
    ),
    street: whenChanged(
      address.street_address,
      defaultShippingAddress.street_address,
    ),

    locale: whenChanged(locale, defaultLocale),
    currency: whenChanged(currency, defaultCurrency),
  };

  const query = new URLSearchParams();

  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined) {
      query.set(key, value);
    }
  }

  const search = query.toString();

  window.history.replaceState(
    null,
    '',
    search ? `?${search}` : window.location.pathname,
  );
}
